import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { POPOVER_ID_DATA_ATTR, POPOVER_REGEX } from '@examdojo/tiptap-popover';
import escape from 'lodash/escape';
import { MarkdownModule } from 'ngx-markdown';
import { MarkdownBlock } from '../../../model';
import { POPOVER_TRIGGER_CLASS } from '../../inline-renderers/popover-trigger-host/popover-trigger-host.component';
import { generateHeadingId, HEADING_REGEX, TOC_ID_DATA_ATTR } from '../../table-of-contents';

@Component({
  selector: 'dojo-markdown-block-renderer',
  templateUrl: './markdown-block-renderer.component.html',
  styleUrl: './markdown-block-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownModule],
})
export class MarkdownBlockRendererComponent {
  private static readonly ICON_REGEX = /\{\{icon:(fa|img):([^:}]+)(?::([^}]*))?\}\}/g;
  private static readonly COLOR_REGEX = /\{\{color:([^}]+)\}\}(.*?)\{\{\/color\}\}/g;

  readonly block = input.required<MarkdownBlock>();

  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
  };

  readonly processedMarkdown = computed(() => {
    const block = this.block();
    let markdown = block.markdown;
    markdown = this.addHeadingIds(markdown, block.id);
    markdown = this.transformPopoverSyntaxToHtml(markdown);
    markdown = this.transformIconSyntaxToHtml(markdown);
    markdown = this.transformColorSyntaxToHtml(markdown);
    return markdown;
  });

  /**
   * Adds data-toc-id spans before each heading for TOC navigation.
   * The span is placed before the heading so it can be used as a scroll target.
   */
  private addHeadingIds(markdown: string, blockId: string): string {
    let headingIndex = 0;
    return markdown.replace(HEADING_REGEX, (match) => {
      const id = generateHeadingId(blockId, headingIndex++);
      return `<span ${TOC_ID_DATA_ATTR}="${id}"></span>\n${match}`;
    });
  }

  /**
   * Transforms popover syntax {{popover:id}}content{{/popover}} into HTML spans
   * that render inline within the markdown content.
   */
  private transformPopoverSyntaxToHtml(markdown: string): string {
    return markdown.replace(POPOVER_REGEX, (_match, popoverId: string, content: string) => {
      return `<span class="${POPOVER_TRIGGER_CLASS}" ${POPOVER_ID_DATA_ATTR}="${escape(popoverId)}">${content}</span>`;
    });
  }

  /**
   * Transforms {{color:value}}text{{/color}} markers into colored spans.
   */
  private transformColorSyntaxToHtml(markdown: string): string {
    return markdown.replace(MarkdownBlockRendererComponent.COLOR_REGEX, (_match, color: string, content: string) => {
      return `<span style="color: ${escape(color)}">${content}</span>`;
    });
  }

  /**
   * Transforms {{icon:fa:name:color}} markers into inline HTML.
   */
  private transformIconSyntaxToHtml(markdown: string): string {
    return markdown.replace(
      MarkdownBlockRendererComponent.ICON_REGEX,
      (_match, type: string, name: string, color?: string) => {
        if (type === 'fa') {
          const colorStyle = color && color !== 'currentColor' ? ` style="color: ${escape(color)}"` : '';
          return `<i class="fas fa-${escape(name)}"${colorStyle}></i>`;
        }
        if (type === 'img') {
          return `<img src="${escape(name)}" alt="" style="width: 1em; height: 1em; vertical-align: middle;" />`;
        }
        return '';
      },
    );
  }
}
